class SizePreferenceManager {
    constructor() {
        this.storageKey = 'wyde_preferred_size';
        this.initialize();
    }

    initialize() {
        this.attachEventListeners();
        this.restoreSavedSize();
        this.observeDynamicContent();
    }

    saveSize(size) {
        try {
            localStorage.setItem(this.storageKey, size);
        } catch (e) {
            console.warn('Could not save size preference:', e);
        }
    }

    getSavedSize() {
        try {
            return localStorage.getItem(this.storageKey);
        } catch (e) {
            console.warn('Could not retrieve size preference:', e);
            return null;
        }
    }

    isSizeOption(inputName) {
        return inputName.split('-')[0].toLowerCase() === 'size';
    }

    attachEventListeners() {
        document.addEventListener('change', this.handleInputChange.bind(this));
        this.attachCustomEventListeners();

        document.addEventListener('variantChange', (event) => {
            const { option } = event.detail;
            if (this.isSizeOption(option?.name)) {
                this.saveSize(option.value);
            }
        });
    }

    handleInputChange(event) {
        const target = event.target;
        if (!target.matches('variant-picker input[type="radio"]')) return;

        const optionName = target.name.split('-')[0];
        const optionData = {
            name: optionName,
            value: target.value
        };

        // Dispatch custom event
        document.dispatchEvent(
            new CustomEvent('variantChange', {
                detail: { option: optionData, element: target }
            })
        );

        if (this.isSizeOption(target.name)) {
            this.saveSize(target.value);
        }
    }

    attachCustomEventListeners() {
        const pickers = document.querySelectorAll('variant-picker');
        pickers.forEach((picker) => this.attachPickerListener(picker));
    }

    attachPickerListener(picker) {
        if (typeof picker.on !== 'function') return;

        picker.on('change', (data) => {
            document.dispatchEvent(
                new CustomEvent('variantChange', {
                    detail: data
                })
            );
        });
    }

    restoreSavedSize() {
        const savedSize = this.getSavedSize();
        if (!savedSize) return;

        setTimeout(() => this.selectSizeOption(savedSize), 100);
    }

    selectSizeOption(size) {
        const pickers = document.querySelectorAll('variant-picker');

        pickers.forEach((picker) => {
            const sizeInput = this.findSizeInput(picker, size);
            if (sizeInput && !sizeInput.disabled) {
                sizeInput.checked = true;
                sizeInput.dispatchEvent(new Event('change', { bubbles: true }));
            }
        });
    }

    findSizeInput(picker, size) {
        const inputs = picker.querySelectorAll('input[type="radio"]');

        return Array.from(inputs).find((input) => this.isSizeOption(input.name) && input.value === size);
    }

    observeDynamicContent() {
        const observer = new MutationObserver(this.handleMutations.bind(this));
        observer.observe(document.body, {
            childList: true,
            subtree: true
        });
    }

    handleMutations(mutations) {
        mutations.forEach((mutation) => {
            mutation.addedNodes.forEach((node) => {
                if (node.nodeType === 1 && node.tagName === 'VARIANT-PICKER') {
                    this.handleNewPicker(node);
                }
            });
        });
    }

    handleNewPicker(picker) {
        const savedSize = this.getSavedSize();
        if (savedSize) {
            setTimeout(() => this.selectSizeOption(savedSize), 100);
        }
        this.attachPickerListener(picker);
    }
}

class SiblingAvailabilityManager {
    constructor() {
        this.siblings = [];
        this.currentSize = null;
        this.hasUserChangedVariant = false;
        this.initialize();
    }

    initialize() {
        this.loadSiblingData();
        this.attachEventListeners();
        this.checkInitialAvailability();
    }

    loadSiblingData() {
        const dataElement = document.querySelector('script[data-sibling-products]');
        if (!dataElement) return;

        try {
            this.siblings = JSON.parse(dataElement.textContent);
        } catch (e) {
            console.error('Failed to parse sibling products data:', e);
        }
    }

    attachEventListeners() {
        document.addEventListener('variantChange', (event) => {
            console.log('Variant changed:', event.detail);
            const { option } = event.detail;

            if (this.isSizeOption(option?.name)) {
                this.hasUserChangedVariant = true;
                this.currentSize = option.value;
                this.updateSiblingAvailability();
            }
        });
    }

    isSizeOption(string) {
        const lowerCaseString = string.toLowerCase();
        const lowerCaseSize = theme.strings.size.toLowerCase();
        const size = 'size';
        if (lowerCaseString.includes(lowerCaseSize) || lowerCaseString.includes(size)) {
            return true;
        }
        return false;
    }

    checkInitialAvailability() {
        const savedSize = localStorage.getItem('wyde_preferred_size');
        const savedSizeExistsInSiblings = this.savedSizeExistsInSiblings(savedSize);

        if (savedSize && savedSizeExistsInSiblings) {
            this.currentSize = savedSize;
            this.hasUserChangedVariant = true;
            setTimeout(() => this.updateSiblingAvailability(), 150);
        }
    }

    savedSizeExistsInSiblings(savedSize) {
        return this.siblings.some((sibling) => {
            const sizeVariant = this.findSizeVariant(sibling, savedSize);
            return sizeVariant !== undefined;
        });
    }

    updateSiblingAvailability() {
        if (!this.currentSize || !this.hasUserChangedVariant) return;

        this.siblings.forEach((sibling) => {
            const isAvailable = this.isSizeAvailableForSibling(sibling, this.currentSize);
            this.updateSiblingUI(sibling.handle, isAvailable);
        });
    }

    findSizeVariant(sibling, size) {
        return sibling.variants.find((variant) => {
            const sizeOptionNames = theme.strings.size_option_names;
            const sizeOption = sizeOptionNames.find((option) => variant.options[option]);
            if (!sizeOption) return false;
            const isSizeOption = variant.options[sizeOption] === size;
            return isSizeOption;
        });
    }

    isSizeAvailableForSibling(sibling, size) {
        const sizeVariant = this.findSizeVariant(sibling, size);
        return sizeVariant.available;
    }

    updateSiblingUI(handle, isAvailable) {
        const siblingElements = document.querySelectorAll(`a[href*="/products/${handle}"]`);

        siblingElements.forEach((element) => {
            if (!isAvailable) {
                element.classList.add('sibling-unavailable');
                element.setAttribute('data-unavailable-size', this.currentSize);
            } else {
                element.classList.remove('sibling-unavailable');
                element.removeAttribute('data-unavailable-size');
            }
        });
    }
}

class SizeGuidePopup {
    constructor() {
        this.triggers = [];
        this.isOpen = false;
        this.initialize();
    }

    initialize() {
        console.log('SizeGuidePopup: Initializing...');
        this.modal = document.getElementById('size-guide-global-modal');
        if (!this.modal) {
            console.error('SizeGuidePopup: Global modal not found');
            return;
        }

        this.initializeTriggers();
        this.attachModalEventListeners();
        this.setupImageModalHandlers();
        this.listenForDynamicContent();
        window.sizeGuidePopup = this;
        console.log('SizeGuidePopup: Initialized with', this.triggers.length, 'triggers');
    }

    initializeTriggers() {
        const triggers = document.querySelectorAll('[data-size-guide-popup-trigger]');
        console.log('SizeGuidePopup: Found', triggers.length, 'triggers');

        this.triggers = Array.from(triggers);
        this.attachTriggerListeners();
    }

    reinitializeTriggers() {
        // Re-scan for triggers and attach listeners
        this.initializeTriggers();
    }

    attachTriggerListeners() {
        // Remove existing listeners to prevent duplicates
        this.triggers.forEach((trigger) => {
            trigger.removeEventListener('click', this.handleTriggerClick);
        });

        // Attach trigger event listeners
        this.triggers.forEach((trigger) => {
            trigger.addEventListener('click', this.handleTriggerClick);
        });
    }

    handleTriggerClick = (e) => {
        e.preventDefault();
        console.log('SizeGuidePopup: Button clicked');
        this.openPopup();
    };

    attachModalEventListeners() {
        // Attach modal event listeners
        const closeBtn = this.modal.querySelector('[data-modal-close]');
        if (closeBtn) {
            closeBtn.addEventListener('click', (e) => {
                e.preventDefault();
                this.closePopup();
            });
        }

        const backdrop = this.modal.querySelector('[data-modal-backdrop]');
        if (backdrop) {
            backdrop.addEventListener('click', (e) => {
                if (e.target === backdrop) {
                    this.closePopup();
                }
            });
        }

        // Close on escape key
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && this.isOpen) {
                this.closePopup();
            }
        });
    }

    openPopup() {
        console.log('SizeGuidePopup: Opening modal');

        // Store scroll position and lock body
        this.scrollY = window.scrollY;
        document.body.style.position = 'fixed';
        document.body.style.top = `-${this.scrollY}px`;
        document.body.style.width = '100%';
        document.body.classList.add('modal-open');

        // Show the modal
        this.modal.removeAttribute('hidden');
        this.isOpen = true;

        // Focus management
        const firstFocusable = this.modal.querySelector('[data-modal-close]');
        if (firstFocusable) {
            setTimeout(() => firstFocusable.focus(), 100);
        }
    }

    closePopup() {
        if (!this.isOpen) return;

        // Hide the modal
        this.modal.setAttribute('hidden', '');

        // Restore body scroll
        document.body.classList.remove('modal-open');
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';

        if (this.scrollY !== undefined) {
            window.scrollTo(0, this.scrollY);
        }

        this.isOpen = false;
    }

    // Add method to handle image modal interactions
    setupImageModalHandlers() {
        // No longer hiding the size guide modal when image modal opens
        // Both modals can be open simultaneously
    }

    listenForDynamicContent() {
        // Listen for landing page product loaded events
        document.addEventListener('landing-page-product:loaded', () => {
            console.log('SizeGuidePopup: Reinitializing triggers after dynamic content load');
            this.reinitializeTriggers();
        });
    }
}

// Sticky Checkout Mobile Custom Component
class StickyCheckoutMobile extends HTMLElement {
    constructor() {
        super();
        this.originalCheckout = null;
        this.originalButton = null;
        this.stickyButton = null;
        this.isVisible = false;
        this.mutationObserver = null;
        this.intersectionObserver = null;
        this.isCheckoutVisible = false;
    }

    connectedCallback() {
        // Only initialize on mobile
        if (window.innerWidth > 768) {
            return;
        }

        this.init();
    }

    disconnectedCallback() {
        if (this.mutationObserver) {
            this.mutationObserver.disconnect();
        }
        if (this.intersectionObserver) {
            this.intersectionObserver.disconnect();
        }
        window.removeEventListener('resize', this.handleResize);
    }

    init() {
        this.originalCheckout = document.querySelector('.cart-subtotal__buttons');
        this.originalButton = document.querySelector('[data-cart-page-checkout-button]');
        this.stickyButton = this.querySelector('[data-sticky-checkout-button]');

        if (!this.originalCheckout || !this.originalButton || !this.stickyButton) {
            return;
        }

        this.setupMutationObserver();
        this.setupIntersectionObserver();
        this.attachEventListeners();
        this.syncButtonState();
    }

    setupMutationObserver() {
        // Watch for changes to the original checkout button for syncing state
        this.mutationObserver = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type === 'attributes' || mutation.type === 'childList') {
                    this.syncButtonState();
                }
            });
        });

        // Observe the original button for state changes
        this.mutationObserver.observe(this.originalButton, {
            attributes: true,
            childList: true,
            subtree: true,
            attributeFilter: ['disabled', 'aria-label']
        });

        // Also observe cart updates
        const cartForm = document.getElementById('CartPageForm');
        if (cartForm) {
            this.mutationObserver.observe(cartForm, {
                childList: true,
                subtree: true
            });
        }
    }

    setupIntersectionObserver() {
        // Use intersection observer to watch when checkout button is visible
        this.intersectionObserver = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    this.isCheckoutVisible = entry.isIntersecting;
                    this.updateVisibility();
                });
            },
            {
                // Show sticky when original is about to go out of view
                rootMargin: '0px 0px -100px 0px', // Trigger when 100px from bottom of viewport
                threshold: [0, 0.1, 1] // Multiple thresholds for better detection
            }
        );

        this.intersectionObserver.observe(this.originalCheckout);
    }

    syncButtonState() {
        if (!this.originalButton || !this.stickyButton) return;

        // Sync disabled state
        if (this.originalButton.disabled) {
            this.stickyButton.disabled = true;
        } else {
            this.stickyButton.disabled = false;
        }

        // Sync aria-label
        const ariaLabel = this.originalButton.getAttribute('aria-label');
        if (ariaLabel) {
            this.stickyButton.setAttribute('aria-label', ariaLabel);
        }

        // Sync button text
        const originalText = this.originalButton.querySelector('[data-button-text]');
        const stickyText = this.stickyButton.querySelector('[data-button-text]');
        if (originalText && stickyText) {
            stickyText.textContent = originalText.textContent;
        }

        // Update cart total and count
        this.updateCartInfo();
    }

    updateCartInfo() {
        // Update total from cart object or DOM
        const cartTotalElement = document.querySelector('.cart-subtotal__total-amount');
        const stickyTotal = this.querySelector('[data-cart-total]');
        if (cartTotalElement && stickyTotal) {
            stickyTotal.textContent = cartTotalElement.textContent;
        }

        // Update item count
        const cartCountElement = document.querySelector('.cart-items__header-product-count');
        const stickyCount = this.querySelector('[data-cart-count]');
        if (cartCountElement && stickyCount) {
            stickyCount.textContent = cartCountElement.textContent;
        }
    }

    attachEventListeners() {
        this.handleResize = this.debounce(() => {
            if (window.innerWidth > 768) {
                this.hide();
                return;
            }
        }, 250);

        window.addEventListener('resize', this.handleResize);
    }

    updateVisibility() {
        if (window.innerWidth > 768) {
            this.hide();
            return;
        }

        // Show sticky when original checkout is NOT visible (out of viewport)
        const shouldShow = !this.isCheckoutVisible;

        if (shouldShow && !this.isVisible) {
            this.show();
        } else if (!shouldShow && this.isVisible) {
            this.hide();
        }
    }

    show() {
        this.removeAttribute('hidden');
        this.isVisible = true;

        requestAnimationFrame(() => {
            this.classList.add('show');
        });
    }

    hide() {
        this.classList.remove('show');
        this.isVisible = false;

        setTimeout(() => {
            if (!this.classList.contains('show')) {
                this.setAttribute('hidden', '');
            }
        }, 300);
    }

    // Utility function
    debounce(func, delay) {
        let timeoutId;
        return function (...args) {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => func.apply(this, args), delay);
        };
    }
}

// Define the custom element
customElements.define('sticky-checkout-mobile', StickyCheckoutMobile);

document.addEventListener('DOMContentLoaded', () => {
    new SizePreferenceManager();
    new SiblingAvailabilityManager();
    new SizeGuidePopup();
});
